import { Accessibility, type DragDropManagerInput, Feedback } from '@dnd-kit/dom'
import { DragDropProvider, PointerSensor } from '@dnd-kit/react'
import { ComponentProps, ReactNode } from 'react'

import { AstraParagraph, astraTwMerge, Cell, DataGrid, DataGridProvider, getToday, Id, isSameDate, PopoverProvider, RowType, SelectionMode } from '~/astra'
import env from '~/env'
import { useFlag } from '~/hooks/useFlag'
import { useStore } from '~/store/store'
import { IdDate, useDateRangeDates, useIsStaffingPlanExecutionBlocking } from '~/storm'
import { getHolidayStyles } from '~/utils/utils'

import { blockedDataGridStyles, getFridayStyles } from '../styles'
import { DateHeaderCell } from './DateHeaderCell'

type DataGridType<Col extends { id: Id }, Row extends { id: Id }> = ComponentProps<typeof DataGrid<Row, Col>>

const datesGridDndSensors = [PointerSensor]

const datesGridDndPlugins: DragDropManagerInput['plugins'] = defaults =>
    defaults
        // The Accessibility aria labelling plugin causes dramatic performance issues. However, DatesGrid only
        // supports PointerSensor and not KeyboardSensor, so we can safely disable it.
        .filter(plugin => plugin !== Accessibility)
        .map(plugin => (plugin === Feedback ? Feedback.configure({ feedback: 'clone', dropAnimation: null }) : plugin))

type Props<Row extends { id: Id }> = {
    isLoading: boolean
    name: string
    className?: string
    rows: Row[]

    children?: ReactNode
    headers?: (RowType<IdDate> | null)[]
    body: DataGridType<IdDate, Row>['body']
    footers?: (RowType<IdDate> | null)[]

    selectionMode?: SelectionMode
    modalContent?: DataGridType<IdDate, Row>['modalContentRender']
    keyboardInteractionComponent?: DataGridType<IdDate, Row>['keyboardInteractionComponent']
    optimizeRendering?: DataGridType<IdDate, Row>['optimizeRendering']

    disableInteractivity?: boolean
    selectedCells?: Cell[]
    onSelectionChange?: (cells: Cell[]) => void
}

export const DatesGrid = <Row extends { id: Id }>({
    isLoading,
    name,
    rows,
    className,
    headers = [],
    body,
    footers = [],
    selectionMode = 'multiple',
    modalContent,
    keyboardInteractionComponent,
    optimizeRendering,
    disableInteractivity,
    selectedCells,
    onSelectionChange,
    children,
}: Props<Row>) => {
    const atLeastSingleExecution = useIsStaffingPlanExecutionBlocking()
    const showTableFooters = useStore(state => state.surgeonScheduler.showTableFooters)
    const fullWidthCells = useStore(state => state.surgeonScheduler.fullWidthCells)
    const dateRange = useStore(state => state.surgeonScheduler.dateRange)
    const openPopover = useStore(state => state.surgeonScheduler.actions.openPopover)
    const dates = useDateRangeDates()
    const isLowPredictedWaitingListIndicator = useFlag('surgeon_scheduler__low_predicted_waiting_list')

    const shouldPreventEditing = atLeastSingleExecution ? blockedDataGridStyles : ''

    const fullWidth = isLoading || fullWidthCells ? 'w-full' : 'w-fit'
    const firstColumnWidth = dateRange === '1' || dateRange === '2' ? 120 : isLowPredictedWaitingListIndicator ? 72 : 64
    const selectionControlProps = selectedCells !== undefined && onSelectionChange !== undefined ? { selectedCells, onSelectionChange } : {}

    return (
        <DragDropProvider sensors={datesGridDndSensors} plugins={datesGridDndPlugins}>
            <DataGridProvider rows={rows} cols={dates} selectionMode={atLeastSingleExecution ? 'none' : selectionMode} {...selectionControlProps}>
                <PopoverProvider noUnstableEffects={env.VITE_NO_UNSTABLE_EFFECTS}>
                    <DataGrid
                        borderless
                        id={name}
                        data-test={!isLoading ? name : ''}
                        cols={dates}
                        rows={rows}
                        fullWidthCells={fullWidthCells}
                        className={`${fullWidth} ${shouldPreventEditing} ${className}`}
                        firstColumnWidth={firstColumnWidth}
                        onClick={() => {
                            if (!atLeastSingleExecution) return
                            openPopover()
                        }}
                        isLoading={isLoading}
                        headers={[
                            {
                                key: 'week-number-header',
                                rowHeader: () => null,
                                cellRender: ({ date }) => {
                                    const shouldShowWeekNumber = dateRange === 'm' ? date.day() === 1 || date.date() === 1 : date.day() === 1
                                    if (!shouldShowWeekNumber) return null

                                    return <AstraParagraph className="p-1 text-center">Uke {date.isoWeek()}</AstraParagraph>
                                },
                                cellClassName: ({ date }) => {
                                    const shouldShowWeekNumber = dateRange === 'm' ? date.day() === 1 || date.date() === 1 : date.day() === 1

                                    return astraTwMerge(
                                        'overflow-visible border-r-0',
                                        getFridayStyles(date),
                                        shouldShowWeekNumber ? 'z-elevated' : '-z-elevated'
                                    )
                                },
                            },
                            {
                                key: 'header',
                                rowHeader: () => null,
                                cellRender: ({ date }) => <DateHeaderCell date={date} />,
                                cellClassName: ({ date }) =>
                                    astraTwMerge(getFridayStyles(date), getHolidayStyles(date), isSameDate(date, getToday()) ? 'calendar-today' : ''),
                            },
                            ...headers,
                        ]}
                        body={body}
                        footers={showTableFooters ? footers : []}
                        // this modal implementation is very opinionated and doesn't allow for more varied use cases.
                        // location is always set to selected cells, which is not always the desired use case
                        modalContentRender={modalContent}
                        keyboardInteractionComponent={keyboardInteractionComponent}
                        optimizeRendering={optimizeRendering}
                        disableInteractivity={disableInteractivity}
                    />
                    {children}
                </PopoverProvider>
            </DataGridProvider>
        </DragDropProvider>
    )
}
